import { track } from '@hex/shared/tracking';
import { Button, Flex, tokens, View, Typography } from '@sapphire-react/core';
import { NumberField } from '@sapphire-react/lab';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LTV_THRESHOLD } from '../../constants';
import { useSapphireMedia } from '../../hooks/useSapphireMedia';
import { ResultWhiteBox } from '../../layout';
import { AffordabilityResponse } from '../../types/affordability';
import { formatMoney } from '../../utils/formatMoney';
import { getLTV } from '../../utils/getLTV';
import { BankLogo } from '../BankLogo';
import { FormDivider } from '../FormDivider';
import { LoanAmountHeading } from './LoanAmountHeading';

type Props = {
  initialLoanAmount?: number;
  affordability: AffordabilityResponse | null;
  downpayment?: number;
  onLoanAmountChange: (value: number) => void;
  dataTrackingId: string;
  calculatedLoanAmount?: number;
};

export function PreliminaryBorrow({
  affordability,
  downpayment,
  initialLoanAmount,
  onLoanAmountChange,
  dataTrackingId,
}: Props) {
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const { t } = useTranslation();

  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';
  const valueSize = isExtraSmall || isSmall ? 'sm' : 'md';
  const labelSize = isExtraSmall || isSmall ? 'xs' : 'sm';

  const disabled = !affordability || !downpayment;
  // had to remove this because affordability endpoint return incorrect LTV. It can be added back if the endpoint is fixed
  // const ltv = useMemo(() => {
  //   if (disabled) {
  //     return undefined;
  //   }
  //   return initialLoanAmount !== affordability?.loanAmount
  //     ? getLTV(
  //         (downpayment + affordability.loanAmount).toString(),
  //         affordability?.loanAmount.toString(),
  //       )
  //     : affordability?.loanToValue;
  // }, [affordability, downpayment, initialLoanAmount]);

  const ltv = useMemo(() => {
    if (disabled) {
      return undefined;
    }

    return getLTV(
      (downpayment + affordability.loanAmount).toString(),
      affordability.loanAmount.toString(),
    );
  }, [affordability, downpayment, disabled]);

  const exceedsLtvTreshold = ltv && ltv > LTV_THRESHOLD;

  return (
    <ResultWhiteBox disabled={disabled} addBottomMargin>
      <Typography.Heading level={4}>
        {t('calculationResult.heading')}
      </Typography.Heading>
      <Typography.Body
        marginTop={tokens.size.spacing3xs}
        size={'sm'}
        textAlign={'center'}
        UNSAFE_style={{
          color: tokens.color.foregroundSecondary.value,
        }}
      >
        {t('calculationResult.description')}
      </Typography.Body>
      <View
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        border={`1px solid ${tokens.color.borderPrimary.value}`}
        width={'100%'}
        marginTop={tokens.size.spacingLg}
        borderRadius={tokens.size.radiusSm}
        UNSAFE_style={{ boxSizing: 'border-box' }}
      >
        <Flex
          flexDirection={'column'}
          width={'100%'}
          paddingLeft={tokens.size.spacingMd}
          paddingRight={tokens.size.spacingMd}
          UNSAFE_style={{ boxSizing: 'border-box' }}
          paddingBottom={tokens.size.spacing2xs}
        >
          <View
            width={12}
            height={12}
            borderRadius={'50%'}
            marginRight={tokens.size.spacing2xs}
            marginTop={5}
          />
          <Flex flexDirection={'column'} width={'100%'}>
            <Flex justifyContent={'space-between'}>
              <Flex alignItems={'center'}>
                <Typography.Body
                  marginRight={tokens.size.spacing3xs}
                  size={'sm'}
                  fontWeight={tokens.font.weightDefaultMedium}
                >
                  {t('calculationResult.loanAmount')}
                </Typography.Body>
              </Flex>
              <Flex alignItems={'center'}>
                <LoanAmountHeading loanAmount={affordability?.loanAmount} />
              </Flex>
            </Flex>
            <FormDivider />

            <Flex
              justifyContent={'space-between'}
              marginBottom={tokens.size.spacing2xs}
            >
              <Flex alignItems={'center'}>
                <Typography.Body
                  size={labelSize}
                  marginRight={tokens.size.spacing3xs}
                  fontWeight={tokens.font.weightDefaultMedium}
                >
                  {t('calculationResult.downPayment')}
                </Typography.Body>
              </Flex>
              <Flex alignItems={'center'}>
                <Typography.Body
                  size={valueSize}
                  data-test-id={'downpayment-result'}
                >
                  {formatMoney(disabled ? undefined : downpayment)}
                </Typography.Body>
              </Flex>
            </Flex>
            <Flex
              justifyContent={'space-between'}
              marginBottom={tokens.size.spacing2xs}
            >
              <Flex alignItems={'center'}>
                <Typography.Body
                  size={labelSize}
                  marginRight={tokens.size.spacing3xs}
                  fontWeight={tokens.font.weightDefaultMedium}
                >
                  {t('calculationResult.propertyPrice')}
                </Typography.Body>
              </Flex>
              <Flex alignItems={'center'}>
                <Typography.Body size={valueSize} data-test-id={'total-result'}>
                  {formatMoney(
                    affordability?.loanAmount && downpayment
                      ? downpayment + affordability?.loanAmount
                      : undefined,
                  )}
                </Typography.Body>
              </Flex>
            </Flex>
            <Flex
              justifyContent={'space-between'}
              marginBottom={tokens.size.spacing2xs}
            >
              <Flex alignItems={'center'}>
                <Typography.Body
                  size={labelSize}
                  marginRight={tokens.size.spacing3xs}
                  fontWeight={tokens.font.weightDefaultMedium}
                >
                  {t('calculationResult.ltv')}
                </Typography.Body>
              </Flex>
              <Flex alignItems={'center'}>
                <Typography.Body
                  size={valueSize}
                  data-test-id={'ltv'}
                  UNSAFE_style={{
                    color: exceedsLtvTreshold
                      ? tokens.color.foregroundOnNegativeSubtle.value
                      : undefined,
                  }}
                >
                  {ltv ? `${ltv} %` : '- %'}
                </Typography.Body>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </View>
      {!disabled && (
        <NumberField
          data-test-id={'loan-amount'}
          alignInputRight
          isDisabled={disabled}
          marginTop={tokens.size.spacingMd.value}
          value={affordability?.loanAmount}
          onChange={onLoanAmountChange}
          maxValue={100_000_000}
          label={t('calculationResult.inputs.adjustLoanAmount.label')}
          width={'100%'}
          size={inputSize}
          affix={{
            postfix: 'kr',
          }}
          error={
            initialLoanAmount &&
            affordability?.loanAmount &&
            affordability?.loanAmount > initialLoanAmount
              ? t('calculationResult.error.loanAmount', {
                  amount: formatMoney(initialLoanAmount),
                })
              : undefined
          }
          note={
            affordability?.loanAmount === initialLoanAmount
              ? undefined
              : t('calculationResult.error.loanAmount', {
                  amount: formatMoney(initialLoanAmount),
                })
          }
        />
      )}

      {/* <MaskedNumberField
        isDisabled={disabled}
        marginTop={tokens.size.spacingMd}
        value={affordability?.loanAmount.toString()}
        onChange={onLoanAmountChange}
        error={
          initialLoanAmount &&
          affordability?.loanAmount &&
          affordability?.loanAmount > initialLoanAmount
            ? `Preliminärt maxbelopp att låna är ${formatMoney(
                initialLoanAmount,
              )}.`
            : undefined
        }
        max={100_000_000}
        label={'Justera lånebeloppet'}
        postfix={'kr'}
        inputMode={'decimal'}
        width={'100%'}
        size={inputSize}
        note={
          affordability?.loanAmount !== initialLoanAmount
            ? `Preliminärt maxbelopp att låna är ${formatMoney(
                initialLoanAmount,
              )}.`
            : undefined
        }
        contextualHelp={
          !disabled ? (
            <ContextualHelp
              variant={'help'}
              aria-label={'Hjälp!'}
              content={
                'Om du justerar lånebeloppet kan du se hur det påverkar bostadens pris, belåningsgraden och månadskostnaden.'
              }
            />
          ) : undefined
        }
      /> */}

      <Button
        marginTop={tokens.size.spacing2xl}
        iconAlign={'right'}
        isDisabled={disabled}
        isHidden={affordability?.loanAmount === 0}
        onPress={() => {
          track(dataTrackingId);
          window.open(
            'https://danskebank.se/bostad/ansok-om-bolan/',
            '_blank',
            'noopener,noreferrer',
          );
        }}
        icon={
          <S.IconContainer>
            <BankLogo />
          </S.IconContainer>
        }
      >
        {t('shared:applyForLoan')}
      </Button>
    </ResultWhiteBox>
  );
}

const S = {
  IconContainer: styled.div`
    box-sizing: border-box;
    padding: 1px;
  `,

  RadioWrapper: styled.div`
    display: flex;
    column-gap: 8px;
  `,
};
